import React, { useEffect, useState } from 'react';

import { TextField } from '@material-ui/core';

import { UiButton } from '../../ui/ui-button';
import { UiSelect } from '../../ui/ui-select';
import { UiTextField } from '../../ui/ui-text-field';
import { useWebhookEventSubscriptionContext } from '../context/webhook-event-subscription-context';

import {
  ACCEPTED_STATUS,
  EMPLOYED_STATUS,
  INTERVIEWING_STATUS,
  PHONE_INTERVIEWING_STATUS,
  statusDisplay,
  VALIDATING_STATUS,
} from '../../config/application-status-config';
import Message from '../../widgets/legacy-message';
import {
  WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TRIGGER_TYPE,
  WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TYPE,
  WebhookEventSubscriptionEventTriggerType,
} from '../utils/webhook-event-subscription-utils';

type InputProps = {
  org: Org;
  errorMessage: string;
  setErrorMessage: (warning: string) => void;
  isDeleting: boolean;
  isEditing: boolean;
};

const emptyCustomKeyValueField = { key: '', value: '' };

const ignoredKeys = ['postUrl', 'useCallback'];
const getCustomKeyValuePairsDefault = (subscriptionDetail: Record<string, any> | null) => {
  const customKeyValuePairs = Object.entries(subscriptionDetail || {}).reduce(
    (acc, [key, value]) => (ignoredKeys.includes(key) ? acc : [...acc, { key, value }]),
    [] as Array<{ key: string; value: string }>,
  );

  return customKeyValuePairs.length ? customKeyValuePairs : [emptyCustomKeyValueField];
};

export const GenericWebhookEventSubscriptionFormBody = (props: InputProps) => {
  const { org, errorMessage, setErrorMessage, isDeleting, isEditing } = props;

  const {
    setEventType,
    eventTriggerType,
    setEventTriggerType,
    subscriptionDetail,
    setSubscriptionDetail,
  } = useWebhookEventSubscriptionContext();

  const [customKeyValuePairs, setCustomKeyValuePairs] = useState<
    Array<{ key: string; value: string }>
  >(getCustomKeyValuePairsDefault(subscriptionDetail));

  // On initial render of this component, set default event values
  useEffect(() => {
    setEventType(WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TYPE.APPLICANT_STATUS_CHANGE);
    // if we are creating a new subscription, set default eventTriggerType
    if (!isEditing) {
      setEventTriggerType(WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TRIGGER_TYPE.VALIDATING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to run on initial render
  }, []);

  const handleChangeTriggerType = (triggerType: string) => {
    setEventTriggerType(triggerType as WebhookEventSubscriptionEventTriggerType);
  };

  const handleSetSubscriptionDetailPostUrl = (postUrl: string) => {
    setSubscriptionDetail({
      ...subscriptionDetail,
      postUrl,
    });
  };

  const handlesetErrorMessage = (
    customKeyValuePairArray: Array<{ key: string; value: string }>,
  ) => {
    const currentKeys = customKeyValuePairArray.map((obj) => obj.key);
    // If we have any duplicate keys that are not empty strings, show a warning
    if (currentKeys.some((key, index) => key && currentKeys.indexOf(key) !== index)) {
      setErrorMessage('Duplicate keys provided. All keys must be unique.');
    } else {
      setErrorMessage('');
    }
  };

  const handleSubscriptionDetailChanges = (
    customKeyValuePairArray: Array<{ key: string; value: string }>,
  ) => {
    const customKeyValuePairMap = customKeyValuePairArray.reduce((acc, obj) => {
      // We don't add a key value pair to subscriptionDetail until both values are defined
      if (!obj.key || !obj.value) return acc;

      acc[obj.key] = obj.value;
      return acc;
    }, {} as Record<string, string>);

    setSubscriptionDetail({
      postUrl: subscriptionDetail?.postUrl || '',
      ...customKeyValuePairMap,
    });
  };

  const handleChangeKeyOrValueInput =
    (index: number) => (event: React.ChangeEvent<{ value: string; name: string }>) => {
      const values = [...customKeyValuePairs];
      values[index][event.target.name as 'key' | 'value'] = event.target.value;
      setCustomKeyValuePairs(values);
      handlesetErrorMessage(values);
      handleSubscriptionDetailChanges(values);
    };

  const handleAddFields = () => {
    setCustomKeyValuePairs([...customKeyValuePairs, { key: '', value: '' }]);
  };

  const handleRemoveFields = (index: number) => () => {
    const values = [...customKeyValuePairs];
    values.splice(index, 1);
    setCustomKeyValuePairs(values);
    handlesetErrorMessage(values);
    // Will remove the kv pair from subscriptionDetail
    handleSubscriptionDetailChanges(values);
  };

  const usesPhoneInterview = org?.usesPhoneInterview || false;
  const triggerTypeStatuses = usesPhoneInterview
    ? [
        ACCEPTED_STATUS,
        PHONE_INTERVIEWING_STATUS,
        INTERVIEWING_STATUS,
        VALIDATING_STATUS,
        EMPLOYED_STATUS,
      ]
    : [ACCEPTED_STATUS, INTERVIEWING_STATUS, VALIDATING_STATUS, EMPLOYED_STATUS];
  const triggerTypeOptions = triggerTypeStatuses.map((opt) => ({
    label: statusDisplay(opt),
    value: opt,
  }));

  return (
    <div>
      <UiSelect
        className="u-margin-top-lg"
        label="Status Change to Send Applicant Data"
        value={eventTriggerType || ''}
        options={triggerTypeOptions}
        onChange={handleChangeTriggerType}
        disabled={isDeleting}
      />
      <UiTextField
        className="u-width-100"
        label="Post URL"
        value={subscriptionDetail?.postUrl || ''}
        onChange={handleSetSubscriptionDetailPostUrl}
        disabled={isDeleting}
      />
      <div className="u-margin-top-xl">
        Add any key value pairs to include in the webhook request. This might include an external
        customer ID, or other required static data.
      </div>
      {errorMessage ? (
        <Message className="u-margin-y-md" variant="error" canBeDismissed={false}>
          <div>{errorMessage}</div>
        </Message>
      ) : null}
      {customKeyValuePairs.map((keyValuePair, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`key-value-pair-${index}`}
          className="u-margin-bottom-lg u-width-100 u-display-flex u-alignItems-flexEnd"
        >
          <TextField
            className="u-margin-right-md u-flexGrow-1"
            name="key"
            label="Key"
            value={keyValuePair.key}
            onChange={handleChangeKeyOrValueInput(index)}
          />
          <TextField
            className="u-margin-right-md u-flexGrow-1"
            name="value"
            label="Value"
            value={keyValuePair.value}
            onChange={handleChangeKeyOrValueInput(index)}
          />
          <UiButton size="sm" variant="tertiary" onClick={handleRemoveFields(index)}>
            Remove Row
          </UiButton>
        </div>
      ))}
      <UiButton onClick={handleAddFields}>Add Row</UiButton>
    </div>
  );
};
